/* ============================================================================
   UI / data — showing records and numbers.
   Stat, StatGrid, DetailList, Avatar, Table, MonthGrid, DayStrip.
   ============================================================================ */
import React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cx } from "./util.js";

/* One number with its label (and an optional hint, icon and tone); put several in a StatGrid. */
export function Stat({ label, value, hint, icon, tone }) {
  const text = (
    <>
      <span className="ds-stat__label">{label}</span>
      <span className="ds-stat__value">{value}</span>
      {hint && <span className="ds-card__sub">{hint}</span>}
    </>
  );
  if (!icon) return <div className="ds-stat">{text}</div>;
  return (
    <div className="ds-stat ds-stat--icon">
      <span className={cx("ds-stat__icon", tone && `is-${tone}`)}>{icon}</span>
      <div className="ds-stat__body">{text}</div>
    </div>
  );
}

/* A responsive row of Stat tiles that wraps on a phone. */
export function StatGrid({ children }) {
  return <div className="ds-stat-grid">{children}</div>;
}

/* A label and its value, in pairs, for the detail of one record. */
export function DetailList({ items, columns = 2 }) {
  return (
    <dl className={cx("ds-details", `ds-details--${columns}`)}>
      {items.map(([label, value]) => (
        <div key={label} className="ds-details__item">
          <dt>{label}</dt>
          <dd>{value ?? "-"}</dd>
        </div>
      ))}
    </dl>
  );
}

/* A person's initials on a tinted disc, or with `src` their photo; the tint follows the name, so a person keeps theirs everywhere. */
const AVATAR_TINTS = 6;
/* A person's initials on a tinted disc, or with `src` their photo; sizes sm, md, lg, xl. */
export function Avatar({ name = "", size = "md", src }) {
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  const letters = ((words[0]?.[0] || "") + (words.length > 1 ? words[words.length - 1][0] : "")).toUpperCase() || "?";
  const tint = [...String(name)].reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % AVATAR_TINTS;
  return <span className={cx("ds-avatar", `ds-avatar--${size}`, `is-tint-${tint}`)} aria-hidden="true">{src ? <img src={src} alt="" /> : letters}</span>;
}

/**
 * columns: [{ key, header, align?, sortable?, className?, render?(row) }]
 * rows:    array of objects (optional `id` for keys)
 * loading: true while the rows are still on their way, so "empty" is not shown as if it were the answer
 * sort / onSort: { key, dir } and a callback with the column key; only columns marked sortable respond
 * onRowClick: makes each row open its record, by click or by Enter
 * stack: on a phone each row becomes a card (the first column is its title, the others are label and value);
 *        a column whose header is empty shows no label, and a column with className "is-card-actions" sits at the foot
 * rowClassName(row): an extra class for a row, such as "is-selected"
 * rowCurrent(row): true for the row that is the one open (a list you pick from); it is announced as the current one
 * renderDetail(row): a full-width line under a row (its transactions, say) when it returns something
 * flush: no frame of its own, for a table that sits inside a card
 * bare: the plain table markup with no look of its own, for a small table the caller styles (inside a dark tooltip)
 * className: an extra class for the table itself, such as one that sets how narrow its columns may get
 * onRowContextMenu(row, event): a right-click on a row (the page opens its own menu)
 * headless: a list of clickable rows that needs no visible heading row; the headings stay for screen readers
 * rowClickable(row): with onRowClick, false for a row that opens nothing (it then looks and acts like a plain row)
 * a column's `group`: columns that follow each other with the same group get one heading above their own, and a rule
 *        where a group starts (the SDR and AE halves of one funnel row, say)
 */
export function Table({ columns, rows = [], empty = "Nothing to show.", loading = false, sort, onSort, onRowClick, rowClickable, stack = false, rowClassName, rowCurrent, renderDetail, flush = false, bare = false, headless = false, className, onRowContextMenu }) {
  const grouped = columns.some((c) => c.group);
  const startsGroup = (c, i) => grouped && i > 0 && c.group !== columns[i - 1].group;
  const groupRuns = [];
  if (grouped) {
    columns.forEach((c, i) => {
      const last = groupRuns[groupRuns.length - 1];
      if (last && last.label === c.group) last.span += 1;
      else groupRuns.push({ key: i, label: c.group, span: 1 });
    });
  }
  const table = (
      <table className={bare ? className : cx("ds-table", stack && "ds-table--stack", headless && "ds-table--headless", className)}>
        <thead>
          {grouped && (
            <tr className="ds-table__groups">{groupRuns.map((run) => (
              <th key={run.key} colSpan={run.span} className={cx("is-center", run.key > 0 && "is-group-start")}>{run.label}</th>
            ))}</tr>
          )}
          <tr>{columns.map((c, i) => {
            const sorted = sort?.key === c.key;
            return (
              <th key={c.key} className={cx(c.align && `is-${c.align}`, startsGroup(c, i) && "is-group-start")} aria-sort={sorted ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}>
                {c.sortable && onSort ? (
                  <button type="button" className="ds-table__sort" onClick={() => onSort(c.key)}>
                    {c.header}
                    {sorted && (sort.dir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                  </button>
                ) : c.header}
              </th>
            );
          })}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="ds-table__empty">{loading ? "Loading…" : empty}</td></tr>
          ) : rows.map((r, i) => {
            const detail = renderDetail?.(r);
            const opens = Boolean(onRowClick) && (!rowClickable || rowClickable(r));
            return (
              <React.Fragment key={r.id ?? i}>
                <tr
                  className={cx(opens && "is-clickable", rowClassName?.(r), detail && "is-open")}
                  aria-current={rowCurrent?.(r) ? "true" : undefined}
                  tabIndex={opens ? 0 : undefined}
                  onContextMenu={onRowContextMenu ? (event) => onRowContextMenu(r, event) : undefined}
                  onClick={opens ? () => onRowClick(r) : undefined}
                  onKeyDown={opens ? (event) => { if (event.key === "Enter" && event.target === event.currentTarget) onRowClick(r); } : undefined}
                >{columns.map((c, ci) => (
                  <td key={c.key} data-label={typeof c.header === "string" ? c.header : undefined} className={cx(c.align && `is-${c.align}`, startsGroup(c, ci) && "is-group-start", c.className)}>{c.render ? c.render(r) : r[c.key]}</td>
                ))}</tr>
                {detail && <tr className="is-detail"><td colSpan={columns.length}>{detail}</td></tr>}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
  );
  return bare ? table : <div className={cx("ds-tablewrap", flush && "ds-tablewrap--flush")}>{table}</div>;
}

/* ── Month grid (ERP-1990) ────────────────────────────────────────────────────
   A month as seven columns of days, for "what happened on each day". days: null for a blank cell before the 1st,
   otherwise { key, label, note, tone, today, muted, title }; tone is success, warning, danger or info. */
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
/* A month as seven columns of days, for "what happened on each day"; see the days shape below. */
export function MonthGrid({ days }) {
  return (
    <div className="ds-monthgrid">
      {WEEKDAYS.map((d) => <span key={d} className="ds-monthgrid__head">{d}</span>)}
      {days.map((day, i) => (day ? (
        <div key={day.key} title={day.title} className={cx("ds-monthgrid__day", day.tone && `is-${day.tone}`, day.today && "is-today", day.muted && "is-muted")}>
          <strong>{day.label}</strong>
          {day.note ? <small>{day.note}</small> : null}
        </div>
      ) : <span key={`blank-${i}`} />))}
    </div>
  );
}

/* A short row of days (the last week), each a button that says what happened on it.
   days: [{ key, label, tone, title }]; onSelect(day) when one is pressed. */
export function DayStrip({ days, onSelect }) {
  return (
    <div className="ds-daystrip">
      {days.map((day) => (
        <button key={day.key} type="button" title={day.title} aria-label={day.title} className={cx("ds-daystrip__day", day.tone && `is-${day.tone}`)} onClick={() => onSelect?.(day)}>{day.label}</button>
      ))}
    </div>
  );
}
